/**
 * Festivals and markets: events that are on for whole days, not at an hour.
 *
 * Dependency-free on purpose, the same rule `filmRank.ts`, `runPattern.ts`
 * and `today.ts` live by: the root suite loads it directly, and `today.ts`,
 * `entries.ts` and `shortlist.ts` all have to agree on what "past" and "on
 * today" mean for these rows, so the decision lives in one place they import
 * (with the `.ts` extension, the way `today.ts` imports `places.ts`).
 *
 * ## Why this file exists — Design Börse and the Kürbisfest, 2–4 Oct 2026
 *
 * Both are `type: 'market'` rows from a newsletter, with a run
 * (`run_start`/`run_end`, two or three days) and a showing on each of the
 * days a promoted lead stated a time for. Doug did not see either on the
 * Saturday or the Sunday they were on. Three readers each took one value to
 * mean two things, and each reading was reasonable alone:
 *
 *  - **`run_start !== run_end` meant "a continuous run"**, the way an
 *    exhibition is, so Today filed the row once, on its opening day, and never
 *    on a day it was merely *on*. For an exhibition that restraint is the
 *    point; for a three-day fair it hid the fair on all three days.
 *  - **The last showing's start meant "the event's end"**, so a row went past
 *    the moment its last showing started. A lead with no time is written as
 *    `T00:00`, so a two-day festival left the app at 00:01 on its last day,
 *    and one with a stated 11:00 left at 11:01 while the stalls were open all
 *    afternoon.
 *  - **A stated hour meant "this is an evening thing"**, so a daytime market
 *    could never be Tonight.
 *
 * ## The rule
 *
 * A **day-long** row is a `festival` or a `market`. Its showings, if it has
 * any, say when somebody stated a time on a given day; they do not say when
 * it stops. It is over when its last *day* is over: `run_end < today`, in
 * Berlin, whatever the clock or a placeholder midnight showing says. A row
 * with no dates at all is not covered, and keeps whatever rule it had.
 *
 * A **span** row is a day-long row whose run covers two to `SPAN_MAX_DAYS`
 * calendar days. It occurs on *each* of those days, from `run_start` to
 * `run_end` inclusive, and has no hour of its own (the hour on a showing is
 * the one a newsletter happened to print for the first or last day).
 *
 * ### Decided: types, not "any short span with discrete showings"
 *
 * Considered and rejected: applying the span rule to any row whose run is
 * short and whose showings are discrete. The live feed on 4 Oct had ~330 such
 * rows, and almost none is a festival: 100 film weeks, 87 theatre runs, 70
 * classical concerts, 30 operas. For those the days a row occurs on are its
 * *showing days*, not every day between the first and the last — a Thursday
 * to Wednesday film week with showings on three days is not "on" the other
 * four — and putting each on every day of its span would turn Today into the
 * programme listing it exists not to be. A festival or a market is the case
 * where the two sets of days coincide, because the venue is open all day.
 * Everything else keeps its showings as the source of its days. A festival or
 * market longer than `SPAN_MAX_DAYS` (a six-week Christmas market) stays a
 * run: listing it on every day of the horizon would be the pile a run's
 * once-only rule exists to prevent.
 *
 * ### Decided (4 Oct, Doug): on every day only up to four days
 *
 * The first cut put every span row on every one of its days, and an eight-day
 * festival (Kunstwoche der Kommunalen Galerien, 2–11 Oct) sat in Today *and*
 * again in each weekday block below it. Being *on* and being *news* were one
 * value. A span of `EVERY_DAY_MAX_DAYS` (4) days or fewer is still on each of
 * its days — a weekend fair is the whole weekend. A longer one (5–14 days)
 * is on its **first day** and on **today** while today is inside it, and
 * nowhere else: the day strip is not the place to say "still going" on six
 * different days. Its end is not covered by Last days either, which is for
 * runs: a span row is never a run (`runOf` is null), and the shortest run
 * Last days takes is fifteen days inclusive (`LAST_DAYS_MIN_RUN`, fourteen
 * day-differences), one more than the longest span. On its last day it is in
 * Today like every other day it is running; nothing marks it as the last.
 * `urgencyLift`'s on-now lift and `alreadyHappened`'s `run_end < today` are
 * about being on and being over, both still true on day six, and are
 * unchanged: they apply to the whole fourteen days.
 *
 * `SPAN_MAX_DAYS` is counted inclusively and is deliberately one under
 * `today.ts`'s `LAST_DAYS_MIN_RUN` (a run of fourteen *day-differences*), so
 * no row is both a span row and a Last days candidate.
 */

/** Types that are on for whole days. */
export const DAYLONG_TYPES: readonly string[] = ['festival', 'market']

/** Most calendar days, inclusive, a span row may cover. */
export const SPAN_MAX_DAYS = 14

/** Most calendar days, inclusive, a span row is on *every* day of. A longer
 *  one (up to `SPAN_MAX_DAYS`) occurs on its first day and on today. */
export const EVERY_DAY_MAX_DAYS = 4

/** The fields these rules read; `FeedRow` and `today.ts`'s `RowEntry` both
 *  satisfy it structurally. */
export interface DaylongRow {
  type: string
  run_start?: string | null
  run_end?: string | null
}

/** Whole days from one `YYYY-MM-DD` to another, at noon UTC so a DST change
 *  cannot move it. */
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000)

/** Is this a festival or a market — a row that is on for whole days? */
export const isDaylong = (e: Pick<DaylongRow, 'type'>): boolean => DAYLONG_TYPES.includes(e.type)

/**
 * The calendar days a span row covers, or null for every other row: not
 * day-long, no dates, a one-day row written the long way, a malformed
 * `run_end` before `run_start`, or longer than `SPAN_MAX_DAYS`.
 */
export function spanOf(e: DaylongRow): { start: string; end: string } | null {
  if (!isDaylong(e) || !e.run_start || !e.run_end || e.run_end <= e.run_start) return null
  if (daysBetween(e.run_start, e.run_end) + 1 > SPAN_MAX_DAYS) return null
  return { start: e.run_start, end: e.run_end }
}

/** Does this span row occur on every day it runs? True up to
 *  `EVERY_DAY_MAX_DAYS` days inclusive. */
const onEveryDay = (s: { start: string; end: string }) =>
  daysBetween(s.start, s.end) + 1 <= EVERY_DAY_MAX_DAYS

/**
 * Is `day` one of the days a span row occurs on? False for every other row —
 * the caller falls back to the row's own occurrence for those.
 *
 * `today` is the Berlin calendar day. A span of up to `EVERY_DAY_MAX_DAYS`
 * days occurs on each day from `run_start` to `run_end`; a longer one on
 * `run_start`, and on `today` while `today` is inside the run. The only
 * place that decides which days those are.
 */
export function spanCovers(e: DaylongRow, day: string, today: string): boolean {
  const s = spanOf(e)
  if (!s || day < s.start || day > s.end) return false
  return onEveryDay(s) || day === s.start || day === today
}

/**
 * The one day a span row is about from `from` (the first day the screen is
 * still about: today, or tomorrow once Tonight has retired), or null.
 *
 * Before the run opens that is its opening day; after it closes, its last.
 * Inside it, an every-day span is `from`; a longer one is `from` only when
 * that is its opening day or today, and otherwise null — "tomorrow, day six
 * of eight" is not news, and null puts it in no section. Only meaningful for
 * a row `spanOf` answers for.
 */
export function spanDayFrom(e: DaylongRow, from: string, today: string): string | null {
  const s = spanOf(e)
  if (!s) return null
  if (from < s.start) return s.start
  if (from > s.end) return s.end
  return spanCovers(e, from, today) ? from : null
}

/**
 * Has a day-long row already ended, or null where this rule does not speak?
 *
 * `today` is the Berlin calendar day, as `alreadyHappened`'s own is. The row
 * is over from the first moment of the day *after* `run_end`, and not before:
 * not when the last showing started, and not at the `T00:00` a lead with no
 * time was written with. A one-day market (`run_start === run_end`, or no
 * `run_end`) is covered too, for the same placeholder.
 *
 * Null means "ask the row's showings", so a dateless row, or one that is not
 * day-long, keeps the old rule rather than this one answering for it.
 */
export function daylongHasEnded(e: DaylongRow, today: string): boolean | null {
  if (!isDaylong(e)) return null
  const last = e.run_end ?? e.run_start
  if (!last) return null
  return last < today
}
